import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS, Icon } from 'components/Icon';
import { Menu, MenuProps } from 'components/Menu';
import { CloudThumbnail } from 'components/Thumbnail/CloudThumbnail';
import { LocalThumbnail } from 'components/Thumbnail/LocalThumbnail';
import { ThumbnailProps } from 'components/Thumbnail/types';
import { TEXT_SIZE_XLARGE, Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import { FileType, FileTypeGroupsEnum } from 'service/file/types';
import {
  selectFileImageGridFit,
  selectFilesShowImageFilename,
} from 'store/files/selectors';
import { ImageGridFitEnums } from 'types/types';
import { BlurView } from 'expo-blur';
import { format } from 'date-fns';
import { numberToDate } from 'lib/riskbase';
import { FileManagerShowImageFilenames, FileManagerViewType } from './types';

type Props = {
  title: React.ReactNode;
  onPress?: () => void;
  thumbnail?: ThumbnailProps;
  chosen?: boolean;
  selectable?: boolean;
  menuOptions?: MenuProps['options'];
  imageWidth: number;
  itemGap: number;

  fileTimestamp: number;
  fileType: FileType;
  viewType: FileManagerViewType;
};

export const FileManagerFilesListItem: React.FC<Props> = React.memo(
  ({
    chosen,
    onPress,
    thumbnail,
    title,
    menuOptions,
    selectable = true,
    imageWidth,
    itemGap,
    fileTimestamp,
    fileType,
    viewType,
  }) => {
    const imageFit =
      fileType.group === FileTypeGroupsEnum.image
        ? // eslint-disable-next-line react-hooks/rules-of-hooks
          useSelector(selectFileImageGridFit)
        : ImageGridFitEnums.square;
    const showFilename =
      fileType.group === FileTypeGroupsEnum.image
        ? // eslint-disable-next-line react-hooks/rules-of-hooks
          useSelector(selectFilesShowImageFilename) ||
          viewType !== FileManagerViewType.grid
        : FileManagerShowImageFilenames.show;

    return (
      <View
        style={{
          marginBottom: itemGap,
          marginTop: itemGap,
          marginRight: itemGap,
          marginLeft: itemGap,
        }}
      >
        <Card
          style={
            viewType === FileManagerViewType.grid
              ? {
                  paddingLeft: 0,
                  paddingRight: 0,
                  paddingTop: 0,
                  paddingBottom: 0,
                  aspectRatio: 1,
                }
              : {
                  flex: 1,
                  marginRight: itemGap,
                  marginLeft: itemGap,
                  marginBottom: itemGap,
                  marginTop: itemGap,
                }
          }
          marginHorizontal={false}
          labelStyle={{ marginLeft: 0, marginRight: 0 }}
          separator={null}
        >
          <CardRow
            onPress={selectable ? onPress : undefined}
            style={[
              styles.row,
              viewType === FileManagerViewType.grid
                ? {
                    paddingLeft: 0,
                    paddingRight: 0,
                    paddingTop: 0,
                    paddingBottom: 0,
                    marginLeft: 0,
                    marginRight: 0,
                  }
                : {
                    paddingLeft: 0,
                  },
            ]}
            hoveredStyle={
              viewType === FileManagerViewType.grid
                ? {
                    opacity: 0.8,
                  }
                : undefined
            }
            menuOptions={menuOptions}
            hideMenuButton={viewType === FileManagerViewType.grid}
            indentActions
            contentStyle={{
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: 0,
              paddingRight: 0,
            }}
            childrenStyle={
              viewType === FileManagerViewType.grid
                ? undefined
                : {
                    flexDirection: 'row',
                    alignItems: 'center',
                  }
            }
          >
            <View style={{ aspectRatio: 1 }}>
              {fileType.group === FileTypeGroupsEnum.image && thumbnail ? (
                thumbnail.imageUrl ? (
                  <CloudThumbnail
                    imageUrl={thumbnail.imageUrl}
                    width="100%"
                    height="100%"
                    contentFit={
                      imageFit === ImageGridFitEnums.aspect
                        ? 'contain'
                        : 'cover'
                    }
                    iconSize={TEXT_SIZE_XLARGE + 12}
                  />
                ) : (
                  <LocalThumbnail
                    thumbnailId={thumbnail.thumbnailId}
                    width="100%"
                    height="100%"
                    contentFit={
                      imageFit === ImageGridFitEnums.aspect
                        ? 'contain'
                        : 'cover'
                    }
                    iconSize={TEXT_SIZE_XLARGE + 12}
                  />
                )
              ) : (
                <View
                  style={{
                    backgroundColor: '#ddd',
                    alignItems: 'center',
                    justifyContent: 'center',
                    aspectRatio: 1,
                    pointerEvents: 'none',
                  }}
                >
                  <Txt size="xlarge" style={{ color: '#bbb' }}>
                    {fileType.extension.toUpperCase()}
                  </Txt>
                </View>
              )}
              {chosen && (
                <Icon
                  source={ICONS.SquareCheckSolid}
                  style={{
                    position: 'absolute',
                    left: theme.paddingHorizontalHalf,
                    top: theme.paddingHorizontalHalf,
                    backgroundColor: theme.colors.white,
                    borderRadius: 4,
                    borderColor: theme.colors.white,
                    borderWidth: 1,
                    overflow: 'hidden',
                  }}
                  size={21}
                  color={theme.colors.blue}
                />
              )}
              {chosen ? (
                <View
                  style={{
                    bottom: 0,
                    left: 0,
                    right: 0,
                    top: 0,
                    position: 'absolute',
                    backgroundColor: theme.colors.blue10,
                    zIndex: 1,
                    pointerEvents: 'none',
                  }}
                />
              ) : !selectable ? (
                <View
                  style={{
                    bottom: 0,
                    left: 0,
                    right: 0,
                    top: 0,
                    position: 'absolute',
                    backgroundColor: theme.colors.background50,
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}
                />
              ) : null}
            </View>

            {viewType === FileManagerViewType.grid && (
              <View
                style={{
                  bottom: theme.paddingHorizontalHalf,
                  left: theme.paddingHorizontalHalf,
                  right: theme.paddingHorizontalHalf,
                  position: 'absolute',
                  flexDirection: 'row',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                }}
              >
                {imageWidth > 100 &&
                showFilename === FileManagerShowImageFilenames.show ? (
                  <BlurView
                    style={{
                      flexShrink: 1,
                      borderRadius: theme.cardBorderRadius - 2,
                      paddingHorizontal: theme.paddingHorizontalHalf,
                      paddingVertical: theme.paddingHorizontalHalf / 2,
                      marginRight: theme.paddingHorizontalHalf,
                      justifyContent: 'center',
                      minHeight: 24,
                    }}
                    intensity={
                      fileType.group === FileTypeGroupsEnum.image ? 10 : 0
                    }
                  >
                    <Txt
                      size="xxxsmall"
                      weight="bold"
                      style={
                        fileType.group === FileTypeGroupsEnum.image
                          ? {
                              textShadowColor: '#fff',
                              textShadowOffset: { width: 0, height: 0 },
                              textShadowRadius: 4,
                            }
                          : undefined
                      }
                      numberOfLines={
                        fileType.group === 'image'
                          ? imageWidth <= 150
                            ? 1
                            : 1
                          : 3
                      }
                    >
                      {title}
                    </Txt>
                  </BlurView>
                ) : (
                  <View style={{ flex: 1 }} />
                )}
                {menuOptions && (
                  <Menu
                    iconButtonConfig={{
                      icon: ICONS.EllipsisLight,
                      style: { backgroundColor: theme.colors.white80 },
                      hoveredStyle: {
                        backgroundColor: theme.colors.white80,
                        opacity: 1,
                      },
                      pressedStyle: { backgroundColor: theme.colors.white50 },
                      size: 24,
                    }}
                    options={menuOptions}
                  />
                )}
              </View>
            )}
            {viewType !== FileManagerViewType.grid && (
              <View style={styles.info}>
                <Txt numberOfLines={1}>{title}</Txt>
                <Txt size="xsmall" numberOfLines={1}>
                  {fileType.extension.toUpperCase()}
                  {' • '}
                  {format(new Date(numberToDate(fileTimestamp)), 'd MMMM yyyy')}
                </Txt>
              </View>
            )}
          </CardRow>
        </Card>
      </View>
    );
  },
  (prev: Props, next: Props): boolean =>
    prev.chosen === next.chosen &&
    prev.title === next.title &&
    prev.imageWidth === next.imageWidth &&
    prev.itemGap === next.itemGap,
);

const styles = StyleSheet.create({
  info: {
    flex: 1,
    overflow: 'hidden',
    marginLeft: theme.padding,
  },
  row: {
    minHeight: 0,
    paddingVertical: 0,
    paddingBottom: 0,
  },
  backgroundColorSelected: {
    backgroundColor: theme.colors.blue10,
  },
});
